// GraphQL documents and their result types.

export const TIMELINE_TYPES = `[PULL_REQUEST_COMMIT, HEAD_REF_FORCE_PUSHED_EVENT, BASE_REF_FORCE_PUSHED_EVENT, REVIEW_REQUESTED_EVENT, REVIEW_REQUEST_REMOVED_EVENT, PULL_REQUEST_REVIEW, ISSUE_COMMENT, READY_FOR_REVIEW_EVENT, CONVERT_TO_DRAFT_EVENT, MERGED_EVENT, CLOSED_EVENT, REOPENED_EVENT]`;

const ACTOR = `{ __typename login avatarUrl }`;
const REVIEWER = `{ __typename ... on User { login avatarUrl } ... on Bot { login avatarUrl } ... on Team { slug name } ... on Mannequin { login } }`;

const TIMELINE_ITEM = `
  __typename
  ... on PullRequestCommit { id commit { oid abbreviatedOid messageHeadline committedDate parents { totalCount } author { name user { login avatarUrl } } } }
  ... on HeadRefForcePushedEvent { id createdAt actor ${ACTOR} beforeCommit { oid abbreviatedOid } afterCommit { oid abbreviatedOid } }
  ... on BaseRefForcePushedEvent { id createdAt actor ${ACTOR} beforeCommit { oid abbreviatedOid } afterCommit { oid abbreviatedOid } }
  ... on ReviewRequestedEvent { id createdAt actor ${ACTOR} requestedReviewer ${REVIEWER} }
  ... on ReviewRequestRemovedEvent { id createdAt actor ${ACTOR} requestedReviewer ${REVIEWER} }
  ... on PullRequestReview { id createdAt submittedAt state body bodyHTML url author ${ACTOR} comments { totalCount } reviewCommit: commit { oid } }
  ... on IssueComment { id databaseId createdAt lastEditedAt body bodyHTML url viewerCanUpdate author ${ACTOR} authorAssociation }
  ... on ReadyForReviewEvent { id createdAt actor ${ACTOR} }
  ... on ConvertToDraftEvent { id createdAt actor ${ACTOR} }
  ... on MergedEvent { id createdAt actor ${ACTOR} mergeCommit: commit { oid abbreviatedOid } }
  ... on ClosedEvent { id createdAt actor ${ACTOR} }
  ... on ReopenedEvent { id createdAt actor ${ACTOR} }
`;

const THREAD_COMMENT = `
  id databaseId body bodyHTML createdAt url
  author ${ACTOR}
  pullRequestReview { id state }
  originalCommit { oid }
  diffHunk
`;

const THREAD = `
  id isResolved isOutdated path line originalLine startLine originalStartLine diffSide startDiffSide
  subjectType viewerCanResolve viewerCanUnresolve viewerCanReply
  resolvedBy { login }
  comments(first: 50) {
    totalCount
    pageInfo { hasNextPage endCursor }
    nodes { ${THREAD_COMMENT} }
  }
`;

/** Further comments of one review thread (threads longer than one page). */
export const THREAD_COMMENTS_PAGE = `
query ThreadComments($id: ID!, $after: String) {
  node(id: $id) { ... on PullRequestReviewThread {
    comments(first: 100, after: $after) { pageInfo { hasNextPage endCursor } nodes { ${THREAD_COMMENT} } }
  } }
}`;

/** An issue or pull request another one names: enough to title a reference to it. */
const REFERENCED = `__typename ... on Issue { number title url repository { nameWithOwner } } ... on PullRequest { number title url repository { nameWithOwner } }`;

/**
 * An issue a pull request closes, the same fields read directly: those
 * nodes are `Issue`s, and GitHub rejects a `... on PullRequest` fragment
 * there (`cannotSpreadFragment`), failing the whole query.
 */
const CLOSED_ISSUE = `__typename number title url repository { nameWithOwner }`;

/**
 * Issues and pull requests related to this one, for titling the references
 * in its conversation (logic/links.ts): those it closes (`closing`), those
 * that mention it, and those connected to it by hand, one bounded page
 * each. A query of its own, so a server that rejects a field (an older
 * GitHub Enterprise Server has no `closingIssuesReferences`) costs only
 * these titles, never the pull request.
 */
export function prRelatedQuery(closing: boolean): string {
  return `
query PullRequestRelated($owner: String!, $repo: String!, $number: Int!) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) {
    ${closing ? `closingIssuesReferences(first: 25) { nodes { ${CLOSED_ISSUE} } }` : ''}
    references: timelineItems(last: 50, itemTypes: [CROSS_REFERENCED_EVENT, CONNECTED_EVENT]) { nodes {
      __typename
      ... on CrossReferencedEvent { source { ${REFERENCED} } }
      ... on ConnectedEvent { subject { ${REFERENCED} } }
    } }
  } }
}`;
}

/** The pull request's latest commits whose deployments PR_DEPLOYMENTS reads (an earlier one's deployment shows as stale). */
export const DEPLOYED_COMMITS = 5;

/**
 * GitHub deployments of the pull request's latest commits, for its
 * preview links (logic/previews.ts). About 1 point. A query of its own,
 * so a failure costs only the previews.
 */
export const PR_DEPLOYMENTS = `
query PullRequestDeployments($owner: String!, $repo: String!, $number: Int!) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) {
    commits(last: ${DEPLOYED_COMMITS}) { nodes { commit { oid deployments(last: 10) { nodes {
      id environment createdAt commitOid state latestStatus { state environmentUrl logUrl createdAt }
    } } } } }
  } }
}`;


const CHECK_CONTEXT = `
  __typename
  ... on CheckRun { databaseId name status conclusion detailsUrl startedAt completedAt
    checkSuite { app { name } workflowRun { workflow { name } } } }
  ... on StatusContext { context state targetUrl description createdAt }
`;

const CHECKS = `
  commits(last: 1) {
    nodes { commit { oid statusCheckRollup { state contexts(first: 100) {
      totalCount pageInfo { hasNextPage endCursor } nodes { ${CHECK_CONTEXT} }
    } } } }
  }
`;

/** The head commit's checks alone (refetched on check notices and paged). */
export const PR_CHECKS = `
query PullRequestChecks($owner: String!, $repo: String!, $number: Int!, $after: String) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) {
    headRefOid
    commits(last: 1) {
      nodes { commit { oid statusCheckRollup { state contexts(first: 100, after: $after) {
        totalCount pageInfo { hasNextPage endCursor } nodes { ${CHECK_CONTEXT} }
      } } } }
    }
  } }
}`;

/**
 * One commit's checks (the relay's `checks` at a commit other than the
 * head, relayTools.ts), paged as PR_CHECKS pages the head's.
 */
export const COMMIT_CHECKS = `
query CommitChecks($owner: String!, $repo: String!, $oid: GitObjectID!, $after: String) {
  repository(owner: $owner, name: $repo) { object(oid: $oid) { ... on Commit {
    oid statusCheckRollup { state contexts(first: 100, after: $after) {
      totalCount pageInfo { hasNextPage endCursor } nodes { ${CHECK_CONTEXT} }
    } }
  } } }
}`;

/**
 * The viewer's "Viewed" checkbox per changed file (GitHub's
 * `viewerViewedState`), 100 files a page: asked for with the pull request
 * (`PR_QUERY`, `$viewed`) and further pages by `PR_VIEWED_PAGE`.
 */
const viewedFiles = (after: string) =>
  `files(first: 100${after}) @include(if: $viewed) { pageInfo { hasNextPage endCursor } nodes { path viewerViewedState } }`;

/**
 * Labels read with a pull request: the first LABELS_PER_PR, in GitHub's
 * order, with how many it has in all (`totalCount`). Past that the rest are
 * only counted: a row says "+N", and the dashboard's Label filter does not
 * see them. Ten covers what repositories put on a pull request in practice.
 */
export const LABELS_PER_PR = 10;
const LABELS = `labels(first: ${LABELS_PER_PR}) { totalCount nodes { name color description } }`;

/** A GitHub label: its name, its colour as six lowercase hex digits, and what it is for. */
export interface Label {
  name: string;
  color: string;
  description: string | null;
}

/** The labels read of a pull request, and how many it has. */
export interface Labels {
  totalCount: number;
  nodes: Label[];
}

export const PR_QUERY = `
query PullRequest($owner: String!, $repo: String!, $number: Int!, $viewed: Boolean = false) {
  viewer { login }
  repository(owner: $owner, name: $repo) {
    id nameWithOwner name defaultBranchRef { name }
    owner { __typename login }
    isPrivate mergeCommitAllowed squashMergeAllowed rebaseMergeAllowed viewerPermission
    pullRequest(number: $number) {
      id databaseId number url title body bodyHTML createdAt updatedAt
      author ${ACTOR} authorAssociation
      state isDraft merged
      baseRefOid headRefOid baseRefName headRefName headRepository { nameWithOwner } headRef { name }
      reviewDecision mergeable mergeStateStatus viewerCanUpdate
      additions deletions changedFiles
      baseRef { branchProtectionRule { requiredApprovingReviewCount } }
      reviewRequests(first: 50) { nodes { requestedReviewer ${REVIEWER} } }
      ${LABELS}
      latestReviews(first: 50) { nodes { id state submittedAt author ${ACTOR} } }
      pending: reviews(first: 1, states: PENDING) { nodes { id databaseId viewerDidAuthor reviewCommit: commit { oid } comments { totalCount } } }
      reviewThreads(first: 100) { totalCount pageInfo { hasNextPage endCursor } nodes { ${THREAD} } }
      ${CHECKS}
      timelineItems(last: 100, itemTypes: ${TIMELINE_TYPES}) { totalCount pageInfo { hasPreviousPage startCursor } nodes { ${TIMELINE_ITEM} } }
      ${viewedFiles('')}
    }
  }
}`;

/** More of the viewer's viewed states (see `viewedFiles`), with the head they are for. */
export const PR_VIEWED_PAGE = `
query PullRequestViewed($owner: String!, $repo: String!, $number: Int!, $after: String, $viewed: Boolean = true) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) { headRefOid ${viewedFiles(', after: $after')} } }
}`;

export const PR_THREADS_PAGE = `
query PullRequestThreads($owner: String!, $repo: String!, $number: Int!, $after: String) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) {
    reviewThreads(first: 100, after: $after) { pageInfo { hasNextPage endCursor } nodes { ${THREAD} } }
  } }
}`;

export const PR_TIMELINE_PAGE = `
query PullRequestTimeline($owner: String!, $repo: String!, $number: Int!, $before: String) {
  repository(owner: $owner, name: $repo) { pullRequest(number: $number) {
    timelineItems(last: 100, before: $before, itemTypes: ${TIMELINE_TYPES}) { pageInfo { hasPreviousPage startCursor } nodes { ${TIMELINE_ITEM} } }
  } }
}`;

/** Native stack members named per search hit; members past these show without a number. */
const STACK_ENTRIES = 30;

/**
 * GitHub's native stack membership (a public preview): the stack's ID, size
 * and members' node IDs and numbers by position (a member the viewer may
 * not see is listed with a null pull request), and the pull request's
 * position, 1 nearest the base branch. The dashboard reads the members no
 * section loaded by those IDs. Older GitHub Enterprise Server schemas lack
 * these fields; see DASH_SECTION_NO_STACKS.
 */
export const DASH_STACK = `stack { id size entries(first: ${STACK_ENTRIES}) { nodes { position pullRequest { id number } } } } stackEntry { position }`;

/**
 * Dashboard row fields. Each costs GitHub time per row, all of it inside
 * the one request (GitHub gives up on a request after about 10 seconds):
 * so a search reads at most DASH_PAGE rows, and fields the dashboard does
 * not show (`mergeStateStatus`, the costliest, which makes GitHub work out
 * mergeability) are left out. Attention inputs come separately
 * (DASH_ATTENTION). The branch fields find stacks. The repository's owner
 * avatar marks the row's organization, and its privacy and the viewer's
 * permission say whether a missing installation of the App is known to be
 * missing (`explicitAccess` in api/install.ts): scalars of objects already
 * read, so they cost no rate-limit points. Labels (LABELS) are one more
 * connection per row, and assignees (the viewer's assignment, which the row
 * shows beside a review asked of them: logic/involvement.ts) one more; the
 * viewer's latest review is an object, which costs none: a
 * 25-row search stays at one point (GitHub rounds a request's connections,
 * one per row each, to the nearest hundred).
 */
const dashPr = (stack: string) => `
  ... on PullRequest {
    id number title url isDraft state createdAt updatedAt additions deletions
    reviewDecision
    author ${ACTOR}
    repository { nameWithOwner name isPrivate viewerPermission owner { login avatarUrl(size: 40) } }
    baseRefName headRefName headRepository { nameWithOwner }
    ${stack}
    reviewRequests(first: 20) { nodes { requestedReviewer ${REVIEWER} } }
    assignees(first: 10) { nodes { login } }
    viewerLatestReview { state }
    ${LABELS}
    commits(last: 1) { nodes { commit { oid statusCheckRollup { state } } } }
  }
`;

/**
 * A pull request as the pull request page's stack read needs it: what
 * places it (branches, native stack membership) and what the stack menu
 * shows of it (title, state, review decision, checks). A dashboard row has
 * all of it; what only the dashboard shows is left out.
 */
const stackRow = (stack: string) => `
  ... on PullRequest {
    id number title isDraft state updatedAt reviewDecision
    repository { nameWithOwner name owner { login } }
    baseRefName headRefName headRepository { nameWithOwner }
    ${stack}
    commits(last: 1) { nodes { commit { oid statusCheckRollup { state } } } }
  }
`;

/**
 * A pull request's native stack with its members: as DASH_STACK, with each
 * member GitHub shows the viewer as a `stackRow`. A stack longer than
 * STACK_ENTRIES names only its first members.
 */
export const PR_STACK = `stack { id size entries(first: ${STACK_ENTRIES}) { nodes { position pullRequest { ${stackRow('')} } } } } stackEntry { position }`;

/** Open pull requests of the repository PR_STACK_QUERY reads, the most recently updated: what a stack by branches is found among. */
export const STACK_POOL = 50;

const prStackQuery = (own: string, stack: string) => `
query PullRequestStack($owner: String!, $repo: String!, $number: Int!, $first: Int!) {
  repository(owner: $owner, name: $repo) {
    pullRequest(number: $number) { id ${own} }
    pullRequests(states: OPEN, first: $first, orderBy: { field: UPDATED_AT, direction: DESC }) { totalCount nodes { ${stackRow(stack)} } }
  }
}`;

/**
 * What the pull request page finds its stack among, in one request: the
 * pull request's native stack with its members (PR_STACK), and the
 * repository's open pull requests, whose branches say which build on
 * which. Read apart from the pull request (PR_QUERY), so nothing of the
 * page waits on it or fails with it.
 */
export const PR_STACK_QUERY = prStackQuery(PR_STACK, DASH_STACK);

/** PR_STACK_QUERY for a GitHub whose schema has no native stacks: the open pull requests' branches alone. */
export const PR_STACK_QUERY_NO_STACKS = prStackQuery('', '');

/** Rows per dashboard search, and per DASH_ROWS batch. */
export const DASH_PAGE = 25;
/**
 * Matches read per query of an attention section, which keeps only the rows
 * waiting on the viewer, so more are checked: by ID, DASH_IDS_PAGE at a
 * time, then their rows in DASH_ROWS batches.
 */
export const DASH_ATTENTION_HITS = 300;
/** IDs per DASH_SEARCH_IDS page (GitHub's search maximum). */
export const DASH_IDS_PAGE = 100;

const dashSection = (stack: string) => `
query DashboardSection($q: String!, $first: Int!) {
  search(query: $q, type: ISSUE, first: $first) { issueCount nodes { ${dashPr(stack)} } }
}`;

/** One dashboard query of at most DASH_PAGE rows (one request per query, so a slow one fails alone). */
export const DASH_SECTION = dashSection(DASH_STACK);

/** DASH_SECTION for a GitHub whose schema has no native stacks. */
export const DASH_SECTION_NO_STACKS = dashSection('');

/**
 * A page of a long dashboard query's matches, by ID alone: GitHub answers
 * a hundred in about two seconds, where a hundred rows can take longer than
 * it allows.
 */
export const DASH_SEARCH_IDS = `
query DashboardSearchIds($q: String!, $first: Int!, $after: String) {
  search(query: $q, type: ISSUE, first: $first, after: $after) {
    issueCount pageInfo { hasNextPage endCursor } nodes { ... on PullRequest { id } }
  }
}`;

const dashRows = (stack: string) => `
query DashboardRows($ids: [ID!]!) {
  nodes(ids: $ids) { ${dashPr(stack)} }
}`;

/** Dashboard rows by node ID, at most DASH_PAGE per request. */
export const DASH_ROWS = dashRows(DASH_STACK);

/** DASH_ROWS for a GitHub whose schema has no native stacks. */
export const DASH_ROWS_NO_STACKS = dashRows('');

/**
 * Comments read from the start of a review thread for the dashboard's
 * attention inputs: the first *published* one matters, and the viewer's
 * own pending (draft) comments, which GitHub returns too, are skipped, so
 * a few are read.
 */
const DASH_THREAD_START = 4;

/** Timeline items DASH_ATTENTION reads per pull request (its most recent). */
export const DASH_TIMELINE_ITEMS = 60;

/** Attention inputs (threads and recent timeline) for a batch of dashboard rows. */
export const DASH_ATTENTION = `
query DashboardAttention($ids: [ID!]!) {
  nodes(ids: $ids) { ... on PullRequest {
    id updatedAt
    reviewThreads(first: 100) { nodes { isResolved
      first: comments(first: ${DASH_THREAD_START}) { nodes { author { login } pullRequestReview { state } } } } }
    timelineItems(last: ${DASH_TIMELINE_ITEMS}, itemTypes: ${TIMELINE_TYPES}) { pageInfo { hasPreviousPage } nodes { ${TIMELINE_ITEM} } }
  } }
}`;

/**
 * What tells whether kept attention inputs still hold (see
 * state/attentionInputs.ts): the pull request's own `updatedAt` (new
 * commits, reviews, comments, review requests, state changes) and its
 * threads' resolved states, which resolving a thread changes without
 * moving `updatedAt`. About 1 point per 100 pull requests, against about
 * 1 per pull request for DASH_ATTENTION.
 */
export const DASH_ATTENTION_CHECK = `
query DashboardAttentionCheck($ids: [ID!]!) {
  nodes(ids: $ids) { ... on PullRequest { id updatedAt reviewThreads(first: 100) { totalCount nodes { isResolved } } } }
}`;

/** One pull request's DASH_ATTENTION_CHECK answer. */
export interface DashAttentionCheck {
  id: string;
  updatedAt: string;
  reviewThreads: { totalCount: number; nodes: { isResolved: boolean }[] };
}

/** Blob SHAs at a commit for a list of paths (aliases f0…fn, expressions `sha:path`). */
export function blobShasQuery(count: number): string {
  const vars = Array.from({ length: count }, (_, i) => `$e${i}: String!`).join(', ');
  const fields = Array.from({ length: count }, (_, i) => `f${i}: object(expression: $e${i}) { ... on Blob { oid } }`).join('\n');
  return `query BlobShas($owner: String!, $repo: String!, ${vars}) { repository(owner: $owner, name: $repo) { ${fields} } }`;
}

/** History walked from each force-push's before side (the commits it replaced, and some it kept). */
export const REPLACED_HISTORY = 20;
/** Base-branch commits listed to stop those walks (a rebased PR's old base). */
export const REPLACED_BASE_HISTORY = 100;

/**
 * Commits replaced by head force-pushes, for the Files view's timeline:
 * the pull request's current commits (SHAs only), recent base-branch SHAs,
 * and for each `$bN` (a force-push's before side) its message, author and
 * recent history.
 */
export function replacedCommitsQuery(count: number): string {
  const vars = Array.from({ length: count }, (_, i) => `$b${i}: GitObjectID!`).join(', ');
  const meta = 'oid abbreviatedOid messageHeadline committedDate author { name user { login } }';
  const fields = Array.from(
    { length: count },
    (_, i) => `b${i}: object(oid: $b${i}) { ... on Commit { history(first: ${REPLACED_HISTORY}) { nodes { ${meta} } } } }`,
  ).join('\n');
  return `query ReplacedCommits($owner: String!, $repo: String!, $number: Int!, $base: GitObjectID!${count ? ', ' + vars : ''}) {
  repository(owner: $owner, name: $repo) {
    pullRequest(number: $number) { commits(last: 100) { totalCount nodes { commit { oid } } } }
    base: object(oid: $base) { ... on Commit { history(first: ${REPLACED_BASE_HISTORY}) { nodes { oid } } } }
    ${fields}
  }
}`;
}

export interface ReplacedCommitsResult {
  repository: {
    pullRequest: { commits: { totalCount: number; nodes: { commit: { oid: string } }[] } } | null;
    base: { history?: { nodes: { oid: string }[] } } | null;
    [alias: `b${number}`]: { history?: { nodes: CommitMetaNode[] } } | null;
  } | null;
}

export interface CommitMetaNode {
  oid: string;
  abbreviatedOid: string;
  messageHeadline: string;
  committedDate: string;
  author: { name: string | null; user: { login: string } | null } | null;
}

export const REPO_PULLS_QUERY = `
query RepoPulls($owner: String!, $repo: String!) {
  repository(owner: $owner, name: $repo) {
    nameWithOwner
    pullRequests(first: 50, states: OPEN, orderBy: { field: UPDATED_AT, direction: DESC }) {
      nodes { number title url isDraft createdAt updatedAt additions deletions reviewDecision author ${ACTOR}
        commits(last: 1) { nodes { commit { statusCheckRollup { state } } } } }
    }
  }
}`;

/** Branches a page of the Branches tab's listing reads (GraphQL's most). */
export const BRANCH_LIST_PAGE = 100;

/**
 * A page of a repository's branches, for the Branches tab's listing: each
 * ref's name and tip (its commit date, and who wrote and committed it),
 * by cursor. GitHub lists refs by name: `TAG_COMMIT_DATE` orders only
 * `refs/tags/` (probed on github.com, 2026-11-03: it gave branches out of
 * date order), so the tab sorts the listing itself, newest push first.
 * With no nested connection a page is one request: one point of the rate
 * limit (GitHub's least).
 */
export const REPO_BRANCH_LIST = `
query RepoBranchList($owner: String!, $repo: String!, $after: String) {
  repository(owner: $owner, name: $repo) {
    refs(refPrefix: "refs/heads/", first: ${BRANCH_LIST_PAGE}, after: $after) {
      totalCount
      pageInfo { hasNextPage endCursor }
      nodes { name target { __typename oid ... on Commit { committedDate author { user { login } } committer { user { login } } } } }
    }
  }
}`;

/** Branches one details query asks about: 2 connections each, so 25 are 50 requests, one point. */
export const BRANCH_DETAILS_BATCH = 25;

/**
 * The details of `count` branches (`$n0`…, each `refs/heads/<name>`), for
 * the rows the Branches tab shows: each tip's headline and checks rollup,
 * its open pull request (the most recently updated: its node ID, review
 * state, base and counts), the merged pull request that had it as its head
 * and into which branch (a squash or rebase merge into the default branch
 * leaves its commits ahead of it), and, `withCompare`, how it stands to the default branch
 * (`$default`), by GitHub's comparison of the ref (the base) with it (the
 * head). Cost: two connections a ref (open and merged pull requests),
 * 2 × count requests, ÷ 100 rounded to the nearest, at least one point:
 * one point for BRANCH_DETAILS_BATCH. A comparison is no connection, but
 * GitHub works each out (a merge base) within the request's time limit, so
 * a batch that fails is asked again in halves, then without it.
 */
export function branchDetailsQuery(count: number, withCompare: boolean): string {
  const vars = Array.from({ length: count }, (_, i) => `$n${i}: String!`).join(', ');
  const fields = Array.from({ length: count }, (_, i) => `r${i}: ref(qualifiedName: $n${i}) { ...BranchDetail }`).join('\n    ');
  return `query BranchDetails($owner: String!, $repo: String!${withCompare ? ', $default: String!' : ''}, ${vars}) {
  repository(owner: $owner, name: $repo) {
    ${fields}
  }
}
fragment BranchDetail on Ref {
  target { ... on Commit { oid messageHeadline statusCheckRollup { state } } }
  open: associatedPullRequests(first: 1, states: OPEN, orderBy: { field: UPDATED_AT, direction: DESC }) {
    nodes { id number title url isDraft reviewDecision author { login } baseRefName baseRefOid changedFiles additions deletions }
  }
  merged: associatedPullRequests(first: 1, states: MERGED, orderBy: { field: UPDATED_AT, direction: DESC }) { nodes { headRefOid baseRefName } }${withCompare ? '\n  compare(headRef: $default) { aheadBy behindBy }' : ''}
}`;
}

// ---------------------------------------------------------------- mutations

export const DELETE_REVIEW = `
mutation DeleteReview($input: DeletePullRequestReviewInput!) {
  deletePullRequestReview(input: $input) { pullRequestReview { id } }
}`;

export const DELETE_REVIEW_COMMENT = `
mutation DeleteReviewComment($input: DeletePullRequestReviewCommentInput!) {
  deletePullRequestReviewComment(input: $input) { pullRequestReview { id } }
}`;

export const RESOLVE_THREAD = `
mutation Resolve($input: ResolveReviewThreadInput!) { resolveReviewThread(input: $input) { thread { id isResolved } } }`;

export const UNRESOLVE_THREAD = `
mutation Unresolve($input: UnresolveReviewThreadInput!) { unresolveReviewThread(input: $input) { thread { id isResolved } } }`;

export const ADD_COMMENT = `
mutation AddComment($input: AddCommentInput!) { addComment(input: $input) { commentEdge { node { id } } } }`;

/** A pull request's head, by its node ID: checked right before a file is marked viewed (GitHub's mutation names no head). */
export const PR_HEAD = `
query PullRequestHead($id: ID!) { node(id: $id) { ... on PullRequest { headRefOid } } }`;

/** Tick the viewer's "Viewed" checkbox of one file (`input: { pullRequestId, path }`), answering with the head it landed on. */
export const MARK_FILE_VIEWED = `
mutation MarkFileViewed($input: MarkFileAsViewedInput!) { markFileAsViewed(input: $input) { pullRequest { headRefOid } } }`;

/** Clear the viewer's "Viewed" checkbox of one file (`input: { pullRequestId, path }`), answering with the head. */
export const UNMARK_FILE_VIEWED = `
mutation UnmarkFileViewed($input: UnmarkFileAsViewedInput!) { unmarkFileAsViewed(input: $input) { pullRequest { headRefOid } } }`;

export const MERGE_PR = `
mutation Merge($input: MergePullRequestInput!) { mergePullRequest(input: $input) { pullRequest { id merged } } }`;

// ---------------------------------------------------------------- result types

/**
 * GitHub's `CommentAuthorAssociation`: how an author relates to the
 * repository. Code that grants trust must treat any other string as untrusted.
 */
export type AuthorAssociation =
  | 'OWNER'
  | 'MEMBER'
  | 'COLLABORATOR'
  | 'CONTRIBUTOR'
  | 'FIRST_TIME_CONTRIBUTOR'
  | 'FIRST_TIMER'
  | 'MANNEQUIN'
  | 'NONE';

export interface Actor {
  __typename?: string;
  login: string;
  avatarUrl?: string;
}

export type Reviewer =
  | { __typename: 'User' | 'Bot' | 'Mannequin'; login: string; avatarUrl?: string }
  | { __typename: 'Team'; slug: string; name: string };

export interface CommitRef {
  oid: string;
  abbreviatedOid: string;
}

export type TimelineItem =
  | {
      __typename: 'PullRequestCommit';
      id: string;
      commit: CommitRef & {
        messageHeadline: string;
        committedDate: string;
        /** Absent in timelines cached before it was asked for. */
        parents?: { totalCount: number };
        author: { name: string | null; user: { login: string; avatarUrl?: string } | null } | null;
      };
    }
  | {
      __typename: 'HeadRefForcePushedEvent' | 'BaseRefForcePushedEvent';
      id: string;
      createdAt: string;
      actor: Actor | null;
      beforeCommit: CommitRef | null;
      afterCommit: CommitRef | null;
    }
  | {
      __typename: 'ReviewRequestedEvent' | 'ReviewRequestRemovedEvent';
      id: string;
      createdAt: string;
      actor: Actor | null;
      requestedReviewer: Reviewer | null;
    }
  | {
      __typename: 'PullRequestReview';
      id: string;
      createdAt: string;
      submittedAt: string | null;
      state: 'PENDING' | 'COMMENTED' | 'APPROVED' | 'CHANGES_REQUESTED' | 'DISMISSED';
      body: string;
      bodyHTML: string;
      url: string;
      author: Actor | null;
      comments: { totalCount: number };
      /** The commit the review was made on. */
      reviewCommit?: { oid: string } | null;
    }
  | {
      __typename: 'IssueComment';
      id: string;
      databaseId: number;
      createdAt: string;
      /** When the comment was last edited (a bot updating its preview link); absent in timelines read before it was asked for. */
      lastEditedAt?: string | null;
      /** How the author relates to the repository; absent in timelines read before it was asked for. */
      authorAssociation?: AuthorAssociation;
      body: string;
      bodyHTML: string;
      url: string;
      viewerCanUpdate: boolean;
      author: Actor | null;
    }
  | {
      __typename: 'ReadyForReviewEvent' | 'ConvertToDraftEvent' | 'ClosedEvent' | 'ReopenedEvent';
      id: string;
      createdAt: string;
      actor: Actor | null;
    }
  | { __typename: 'MergedEvent'; id: string; createdAt: string; actor: Actor | null; mergeCommit: CommitRef | null };

export interface ThreadComment {
  id: string;
  databaseId: number;
  body: string;
  bodyHTML: string;
  createdAt: string;
  url: string;
  author: Actor | null;
  pullRequestReview: { id: string; state: string } | null;
  originalCommit: { oid: string } | null;
  /**
   * The diff hunk the comment was written on, ending at its original line
   * (a suggestion's original lines are its tail; see `hunkLines`). Absent on
   * comments the client builds itself.
   */
  diffHunk?: string;
}

export interface ReviewThread {
  id: string;
  isResolved: boolean;
  isOutdated: boolean;
  path: string;
  line: number | null;
  originalLine: number | null;
  startLine: number | null;
  originalStartLine: number | null;
  diffSide: 'LEFT' | 'RIGHT';
  startDiffSide: 'LEFT' | 'RIGHT' | null;
  subjectType: 'LINE' | 'FILE';
  viewerCanResolve: boolean;
  viewerCanUnresolve: boolean;
  viewerCanReply: boolean;
  resolvedBy: { login: string } | null;
  comments: { totalCount: number; pageInfo?: PageInfo; nodes: ThreadComment[] };
}

export interface CheckContext {
  __typename: 'CheckRun' | 'StatusContext';
  databaseId?: number;
  name?: string;
  status?: string;
  conclusion?: string | null;
  detailsUrl?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  checkSuite?: { app: { name: string } | null; workflowRun: { workflow: { name: string } } | null } | null;
  context?: string;
  state?: string;
  targetUrl?: string | null;
  description?: string | null;
}

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface BackwardPageInfo {
  hasPreviousPage: boolean;
  startCursor: string | null;
}

export interface CheckContexts {
  totalCount?: number;
  pageInfo?: PageInfo;
  nodes: CheckContext[];
}

export interface PullRequestData {
  id: string;
  databaseId: number;
  number: number;
  url: string;
  title: string;
  body: string;
  bodyHTML: string;
  createdAt: string;
  updatedAt: string;
  author: Actor | null;
  /** The author's relationship to the repository (GitHub: non-null). */
  authorAssociation: AuthorAssociation;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  isDraft: boolean;
  merged: boolean;
  baseRefOid: string;
  headRefOid: string;
  baseRefName: string;
  headRefName: string;
  /** Where the head branch lives; null when that repository is gone (a deleted fork). Absent from fixtures that predate it. */
  headRepository?: { nameWithOwner: string } | null;
  /** The head branch; null once it is deleted (on merge, say), as GitHub says. Absent from fixtures that predate it: taken to be there. */
  headRef?: { name: string } | null;
  reviewDecision: 'APPROVED' | 'CHANGES_REQUESTED' | 'REVIEW_REQUIRED' | null;
  mergeable: 'MERGEABLE' | 'CONFLICTING' | 'UNKNOWN';
  mergeStateStatus: 'BEHIND' | 'BLOCKED' | 'CLEAN' | 'DIRTY' | 'DRAFT' | 'HAS_HOOKS' | 'UNKNOWN' | 'UNSTABLE';
  viewerCanUpdate: boolean;
  additions: number;
  deletions: number;
  changedFiles: number;
  baseRef: { branchProtectionRule: { requiredApprovingReviewCount: number | null } | null } | null;
  /** Its labels (the first LABELS_PER_PR); empty when GitHub leaves them out. */
  labels: Labels;
  reviewRequests: { nodes: { requestedReviewer: Reviewer | null }[] };
  latestReviews: { nodes: { id: string; state: string; submittedAt: string | null; author: Actor | null }[] };
  pending: {
    nodes: {
      id: string;
      databaseId: number;
      viewerDidAuthor: boolean;
      reviewCommit?: { oid: string } | null;
      comments: { totalCount: number };
    }[];
  };
  /** How many there are in all: in PR_QUERY's first page only, for the loading screen's progress. */
  reviewThreads: { totalCount?: number; pageInfo: PageInfo; nodes: ReviewThread[] };
  commits: {
    nodes: {
      commit: {
        oid: string;
        statusCheckRollup: { state: string; contexts: CheckContexts } | null;
      };
    }[];
  };
  /** How many there are in all: in PR_QUERY's first page only, for the loading screen's progress. */
  timelineItems: { totalCount?: number; pageInfo: BackwardPageInfo; nodes: TimelineItem[] };
  /** The viewer's viewed state per file; present only when asked for (`$viewed`). */
  files?: ViewedFiles | null;
}

/** A page of the viewer's viewed states (`viewedFiles`). */
export interface ViewedFiles {
  pageInfo: PageInfo;
  nodes: { path: string; viewerViewedState: 'VIEWED' | 'UNVIEWED' | 'DISMISSED' }[];
}

/** A pull request's related issues, as `prRelatedQuery` reads them. */
export interface RelatedRefs {
  /** Issues it closes when merged; absent when not asked for. */
  closingIssuesReferences?: { nodes: (ReferencedNode | null)[] };
  /** Issues and pull requests that mention it, or were connected to it. */
  references: { nodes: (RelatedEvent | null)[] };
}

export interface RelatedResult {
  repository: { pullRequest: RelatedRefs | null } | null;
}

/** An issue or pull request, as `REFERENCED` reads it. */
export interface ReferencedNode {
  __typename: 'Issue' | 'PullRequest';
  number: number;
  title: string;
  url: string;
  repository: { nameWithOwner: string };
}

export type RelatedEvent =
  | { __typename: 'CrossReferencedEvent'; source: ReferencedNode | null }
  | { __typename: 'ConnectedEvent'; subject: ReferencedNode | null };

export interface RepositoryData {
  id: string;
  nameWithOwner: string;
  name: string;
  owner: { __typename: string; login: string };
  /** The default branch; null for an empty repository. Absent from fixtures that predate it. */
  defaultBranchRef?: { name: string } | null;
  mergeCommitAllowed: boolean;
  squashMergeAllowed: boolean;
  rebaseMergeAllowed: boolean;
  /** Absent in fixtures that predate it; treated as public. */
  isPrivate?: boolean;
  viewerPermission: string | null;
  pullRequest: PullRequestData | null;
}

export interface PrQueryResult {
  viewer: { login: string };
  repository: RepositoryData | null;
}

/** A pull request as the stack read reads it (`stackRow`): the fields of a dashboard row that place it in a stack and that the stack menu shows. */
export type StackRowPr = Pick<
  DashPr,
  'id' | 'number' | 'title' | 'isDraft' | 'state' | 'updatedAt' | 'reviewDecision' | 'repository' | 'baseRefName' | 'headRefName' | 'headRepository' | 'stack' | 'stackEntry' | 'commits'
>;

/** A member of a pull request's native stack as PR_STACK reads it: a `StackRowPr` without stack fields of its own (its place is its entry's). */
export type StackMemberPr = Omit<StackRowPr, 'stack' | 'stackEntry'>;

/** What PR_STACK_QUERY answers; the stack fields are absent where GitHub's schema has none. */
export interface PrStackResult {
  repository: {
    /** Null when GitHub does not show the pull request. A member GitHub does not show the viewer is listed with a null pull request. */
    pullRequest: {
      id: string;
      stack?: { id: string; size: number; entries: { nodes: ({ position: number; pullRequest: StackMemberPr | null } | null)[] } } | null;
      /** Its position in `stack`, 1 nearest the base branch. */
      stackEntry?: { position: number } | null;
    } | null;
    pullRequests: { totalCount: number; nodes: (StackRowPr | null)[] };
  } | null;
}

export interface DashPr {
  id: string;
  number: number;
  title: string;
  url: string;
  isDraft: boolean;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  createdAt: string;
  updatedAt: string;
  additions: number;
  deletions: number;
  reviewDecision: string | null;
  author: Actor | null;
  /** `isPrivate`, `viewerPermission` and the owner's `avatarUrl` are absent from rows saved before they were read. */
  repository: { nameWithOwner: string; name: string; isPrivate?: boolean; viewerPermission?: string | null; owner: { login: string; avatarUrl?: string } };
  baseRefName: string;
  headRefName: string;
  /** Null when the head repository is gone (a deleted fork). */
  headRepository: { nameWithOwner: string } | null;
  /** GitHub's native stack; null outside one, and where GitHub's schema has no stacks. */
  stack: {
    id: string;
    size: number;
    entries: { nodes: ({ position: number; pullRequest: { id?: string; number: number } | null } | null)[] };
  } | null;
  stackEntry: { position: number } | null;
  /** Its labels (the first LABELS_PER_PR), decoded (`readLabels`); absent from rows saved before they were read. */
  labels?: Labels;
  reviewRequests: { nodes: { requestedReviewer: Reviewer | null }[] };
  /** Who it is assigned to (the first 10); absent from rows saved before it was read. */
  assignees?: { nodes: { login: string }[] };
  /** The viewer's latest review of it, null when they gave none; absent from rows saved before it was read. */
  viewerLatestReview?: { state: string } | null;
  /** The head commit (its `oid` is absent from rows saved before it was read) and its checks. */
  commits: { nodes: { commit: { oid?: string; statusCheckRollup: { state: string } | null } }[] };
}

/** DASH_ATTENTION node: the attention inputs for one dashboard row. */
export interface DashThreadComment {
  author: { login: string } | null;
  /** PENDING for the viewer's unpublished draft. */
  pullRequestReview?: { state: string } | null;
}

export interface DashAttentionInputs {
  id: string;
  /** The pull request's own `updatedAt` when read (not the search index's, which lags). */
  updatedAt?: string;
  reviewThreads: {
    nodes: {
      isResolved: boolean;
      first: { nodes: DashThreadComment[] };
    }[];
  };
  /** `pageInfo` is absent from inputs kept from before it was read. */
  timelineItems: { nodes: TimelineItem[]; pageInfo?: { hasPreviousPage: boolean } };
}

/** REST `pulls/:n/files` entry. */
export interface PrFile {
  sha: string | null;
  filename: string;
  status: 'added' | 'removed' | 'modified' | 'renamed' | 'copied' | 'changed' | 'unchanged';
  additions: number;
  deletions: number;
  changes: number;
  patch?: string;
  previous_filename?: string;
}

/** REST check-run annotation. */
export interface Annotation {
  path: string;
  start_line: number;
  end_line: number;
  annotation_level: 'notice' | 'warning' | 'failure';
  title: string | null;
  message: string;
  raw_details?: string | null;
  blob_href?: string;
}
